import React, { useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BsChatDots } from 'react-icons/bs';
import * as translator from '../../../../utils/translator';
import * as http from '../../../../utils/http';
import LoadingModal from '../../../../components/Modals/LoadingModal/LoadingModal';
import ServerError from '../../../../components/ServerError/ServerError';
import { useCustomer } from '../../../../customHooks/useCustomer';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { CareTeamMember } from 'data-types';
import CareProfileHeader from './components/CareProfileHeader';
import { useCareProfile } from './hooks/useCareProfile';
import { localeByLanguage } from './careProfileAssets';
import { supportsImperialWeight, useWeightUnit } from '../../../../utils/weightUnits';
import { useCareProfileTour } from './tour/useCareProfileTour';
import CareProfileTour from './tour/CareProfileTour';
import { LogWeightFormValues } from './components/logWeightSchema';
import WeightProgressCard from './components/WeightProgressCard';
import WeightTrendChart from './components/WeightTrendChart';
import EncouragementWidget from './components/EncouragementWidget';
import GoalReachedCard from './components/GoalReachedCard';
import LogWeightSheet from './components/LogWeightSheet';
import SetGoalSheet, { SetGoalFormValues } from './components/SetGoalSheet';
import ChatWithDoctorSheet from './components/ChatWithDoctorSheet';
import { ChatChannel } from './hooks/useDoctorChat';
import BookConsultationSheet from './components/BookConsultationSheet';
import NutritionSummarySheet from './components/NutritionSummarySheet';
import TreatmentDocumentsSheet from './components/TreatmentDocumentsSheet';
import CareTeamCards from './components/CareTeamCards';
import TreatmentCycleTimeline from './components/TreatmentCycleTimeline';
import UpsellBlock from './components/UpsellBlock';
import QuickLinks from './components/QuickLinks';
import CareProfileGradientButton from './components/CareProfileGradientButton';
import PlanActiveState from './states/PlanActiveState';
import PlanEndedState from './states/PlanEndedState';
import CareProfilePlanCard from './components/CareProfilePlanCard';
import LockedFeatureOverlay from './components/LockedFeatureOverlay';
import { isCareProfilePlanCardTier } from './planFeatures';

const MyAccountCareProfile = () => {
	const { country } = useParams<{ country: string }>();
	// Language, not country: the route carries `gb`, which has no translations folder (falls back to de).
	const language = translator.getLanguageByCountry(country);
	// UK: stones/pounds by default, switchable in the weight sheets; kg everywhere else.
	const [weightUnit, setWeightUnit] = useWeightUnit(country);
	const allowWeightUnitChange = supportsImperialWeight(country);
	const [error, setError] = useState<any>(false);
	const [loading, setLoading] = useState<boolean>(true);
	const [firstName, setFirstName] = useState<string>('');
	// HL-11160: asidebar may still know about a cancelled Care+ plan after the profile API
	// briefly fails to enroll — prefer the ended dashboard over a hard 404.
	const [hadCarePlusEnrollment, setHadCarePlusEnrollment] = useState<boolean>(false);
	const {
		careProfile,
		fetchCareProfile,
		logWeight,
		isSubmittingWeight,
		logWeightError,
		setGoal,
		isSubmittingGoal,
		setGoalError,
		treatmentDocuments,
		fetchTreatmentDocuments,
		isLoadingDocuments,
	} = useCareProfile({ country: country ?? '', setError, setLoading });
	const [isLogWeightOpen, setIsLogWeightOpen] = useState(false);
	const [isSetGoalOpen, setIsSetGoalOpen] = useState(false);
	// Which chat thread is open, if any. Each channel is a separate conversation server-side.
	const [chatChannel, setChatChannel] = useState<ChatChannel | null>(null);
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [chatRole, setChatRole] = useState<string | undefined>(undefined);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [bookingRole, setBookingRole] = useState<'doctor' | 'nutritionist'>('doctor');
	const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
	const [summaryEvent, setSummaryEvent] = useState<{ title: string; date: string } | null>(null);
	const { customerState } = useCustomer();
	const trackedView = useRef(false);
	const isAnySheetOpen = isLogWeightOpen || isSetGoalOpen || Boolean(chatChannel) || isChatOpen || isBookingOpen || isDocumentsOpen || Boolean(summaryEvent);
	// Ended plan (same rule as effectiveDashboardState below, but needed before the early returns
	// so the tour hook can read it): every feature except the renew card, the shop and support
	// chat is locked until the customer renews.
	const isPlanEnded = Boolean(careProfile) && (!careProfile!.enrolled || careProfile!.dashboardState === 'ended');
	const tour = useCareProfileTour({
		customerId: customerState?.customerID ?? null,
		careProfile,
		isBlocked: isAnySheetOpen || isPlanEnded,
	});

	const openTreatmentDocuments = () => {
		if (isPlanEnded) return;
		fetchTreatmentDocuments();
		setIsDocumentsOpen(true);
	};

	const handleBook = (member: CareTeamMember) => {
		if (isPlanEnded) return;
		// Doctor video booking is gated by enable_care_plus_video_consultation.
		// Nutritionist/coach booking is not blocked by that doctor-only kill-switch.
		if (member.role === 'doctor' && !(careProfile?.tierFlags as any)?.doctorVideoConsultationEnabled) return;
		setBookingRole(member.role);
		setIsBookingOpen(true);
	};

	const handleMessage = (member: CareTeamMember) => {
		if (isPlanEnded) return;
		// WhatsApp routing is handled inside CareTeamCards before this is called.
		// Each care team role opens its own thread — the coach must not see doctor history.
		setChatChannel(member.role === 'nutritionist' ? 'nutritionist' : 'doctor');
		setChatRole(member.role);
		setIsChatOpen(true);
	};

	React.useEffect(() => {
		http
			.getFromServer(`${country}/account/asidebar`, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				setFirstName(response.data.firstName);
				// active OR cancelled Care+ keeps a subscription hash on the asidebar (HL-11160).
				if (response.data?.carePlusSubscriptionHashId || response.data?.hasActiveCarePlusSubscription) {
					setHadCarePlusEnrollment(true);
				}
			})
			.catch((error) => window.clientLogger.error('Error in MyAccountCareProfile (asidebar): ', error));

		fetchCareProfile();
	}, []);

	React.useEffect(() => {
		if (!loading && careProfile && !trackedView.current) {
			trackedView.current = true;
			MainTracker.track('pv', 'View dashboard Page', { subscription_status: careProfile.dashboardState });
		}
	}, [loading, careProfile?.dashboardState]);

	const handleLogWeightSubmit = (values: LogWeightFormValues) => {
		logWeight(values, () => setIsLogWeightOpen(false));
	};

	const handleSetGoalSubmit = (values: SetGoalFormValues) => {
		setGoal(values, () => setIsSetGoalOpen(false));
	};

	if (error && !loading) {
		return (
			<>
				<CareProfileHeader />
				<ServerError status={error} />
			</>
		);
	}

	if (loading || !careProfile) {
		return (
			<>
				<CareProfileHeader />
				<LoadingModal />
			</>
		);
	}

	const supportChatLink = careProfile.quickLinks.find((link: any) => link.key === 'support-chat') ?? null;
	const preferredDoctorID = careProfile.careTeam.find((m) => m.role === 'doctor')?.id ?? null;

	// Care+ is the only audience for this page. Never-Care+ customers get 404.
	// HL-11160: cancelled Care+ must still see the ended dashboard (Restart CTA), not 404.
	// Prefer API enrolled=true; if API returns enrolled=false but asidebar still has a Care+
	// hash (cancelled/ended), fall through to PlanEndedState shell.
	if (!careProfile.enrolled && !hadCarePlusEnrollment) {
		return (
			<>
				<CareProfileHeader />
				<ServerError status={404} />
			</>
		);
	}

	const effectiveDashboardState = careProfile.enrolled
		? careProfile.dashboardState
		: 'ended';

	const goalReachedCard = careProfile.weight && careProfile.dashboardState === 'finished' ? (
		<GoalReachedCard
			language={language}
			totalLostKg={careProfile.weight.progress.totalLostKg}
			currentKg={careProfile.weight.progress.currentKg}
			onSetNewGoal={() => setIsSetGoalOpen(true)}
			weightUnit={weightUnit}
		/>
	) : null;

	return (
		<>
			<CareProfileHeader />
			<div id="care-profile-page" className="bg-care-plus-cream flex flex-col gap-y-4">
				<div className="max-w-[1200px] mx-auto w-full px-4 pt-6 lg:px-10">
					{/* The welcome hero stays up while the plan is ended (Figma); the renew card itself
					    leads the left grid column below rather than sitting in this banner slot. */}
					{(effectiveDashboardState === 'active' || effectiveDashboardState === 'ended') && (
						<PlanActiveState firstName={firstName} language={language} isMonthlyPlan={Boolean(careProfile?.tierFlags?.isMonthlyPlan)} />
					)}
					{/* UK only for now: paused-plan banner. */}
					{country?.toLowerCase() === 'gb' && careProfile.plan?.isPaused && !isPlanEnded && (
						<div id="care-profile-paused-banner" className="mt-4 w-full rounded-2xl bg-white px-5 py-4 flex flex-col gap-y-1 lg:flex-row lg:items-center lg:justify-between lg:gap-x-4">
							<div className="flex flex-col gap-y-1">
								<p
									className="text-base text-care-plus-heading"
									dangerouslySetInnerHTML={{ __html: translator.translate(language, 'my_account', 'CarePlus/notifications/paused/title') }}
								/>
								{careProfile.plan.pausedUntil && (
									<p className="text-sm text-[#616975]">
										{(translator.translate(language, 'my_account', 'CarePlus/notifications/paused/body') || '')
											.replace(':date', new Date(careProfile.plan.pausedUntil).toLocaleDateString(localeByLanguage[language] || 'en-GB'))}
									</p>
								)}
							</div>
							<Link to={`/${country}/account/orders-subscription`} className="text-sm font-medium text-care-plus-orange underline">
								{translator.translate(language, 'my_account', 'CarePlus/notifications/paused/cta')}
							</Link>
						</div>
					)}
				</div>

				<div className="max-w-[1520px] mx-auto w-full flex flex-col gap-y-4 px-4 pb-6 lg:px-10">
					<div id="care-profile-grid" className="grid grid-cols-1 gap-4 lg:grid-cols-[354fr_742fr_355fr] lg:gap-x-[30px] lg:items-start">
						<div className="order-1 lg:order-none lg:col-start-1 flex flex-col gap-y-[30px] min-w-0">
							{/* Ended plan: the renew card leads the left column (desktop) / the page (mobile). */}
							{isPlanEnded && (
								<PlanEndedState language={language} toGoalKg={careProfile.weight?.progress.toGoalKg ?? null} weightUnit={weightUnit} isMonthlyPlan={Boolean(careProfile.tierFlags?.isMonthlyPlan)} />
							)}
							<LockedFeatureOverlay locked={isPlanEnded} className="flex flex-col gap-y-[30px]">
							{careProfile.plan && isCareProfilePlanCardTier(careProfile.plan.tier) && (
								<CareProfilePlanCard plan={careProfile.plan} language={language} />
							)}
							{careProfile.weight && (
								<>
									<div id="weight-progress-mobile-card" className="lg:hidden flex flex-col gap-y-[17px] rounded-[20px] border border-solid border-[#e7e4db] bg-white p-3">
										<WeightProgressCard progress={careProfile.weight.progress} language={language} bare weightUnit={weightUnit} />
										{goalReachedCard ?? (
											<EncouragementWidget encouragement={careProfile.weight.encouragement} language={language} />
										)}
									</div>
									<div className="hidden lg:flex lg:flex-col lg:gap-y-[30px]">
										<WeightProgressCard progress={careProfile.weight.progress} language={language} weightUnit={weightUnit} />
										{goalReachedCard ?? (
											<EncouragementWidget encouragement={careProfile.weight.encouragement} language={language} />
										)}
									</div>
								</>
							)}
							</LockedFeatureOverlay>
						</div>

						<div className="order-2 lg:order-none lg:col-start-2 flex flex-col gap-y-[30px] min-w-0">
							<LockedFeatureOverlay locked={isPlanEnded}>
								<WeightTrendChart
									series={careProfile.weight?.series ?? []}
									goalKg={careProfile.weight?.progress.goalKg ?? null}
									language={language}
									onLogWeight={() => { if (!isPlanEnded) setIsLogWeightOpen(true); }}
									weightUnit={weightUnit}
								/>
							</LockedFeatureOverlay>
							{/* Mobile: Care Team appears between Chart and Timeline */}
							<div className="lg:hidden">
								<LockedFeatureOverlay locked={isPlanEnded} showLock>
									<CareTeamCards
										id="care-team-cards-mobile"
										members={careProfile.careTeam}
										tierFlags={careProfile.tierFlags}
										language={language}
										country={country ?? ''}
										onMessage={handleMessage}
										onBook={handleBook}
									/>
								</LockedFeatureOverlay>
							</div>
							<LockedFeatureOverlay locked={isPlanEnded}>
								<TreatmentCycleTimeline
									upcoming={careProfile.timeline.upcoming}
									past={careProfile.timeline.past}
									country={country ?? ''}
									language={language}
									// event.title is a translation key from the server; resolve it for display.
									onOpenSummary={(event) => {
										if (isPlanEnded) return;
										setSummaryEvent({ title: translator.translate(language, 'my_account', event.title), date: event.date });
									}}
									onOpenBooking={(event) => {
										if (isPlanEnded) return;
										const role = event.providerRole === 'nutritionist' ? 'nutritionist' : 'doctor';
										// Doctor-only kill-switch — nutritionist timeline booking stays available.
										if (role === 'doctor' && !(careProfile.tierFlags as any).doctorVideoConsultationEnabled) return;
										setBookingRole(role);
										setIsBookingOpen(true);
									}}
								/>
							</LockedFeatureOverlay>
						</div>

						<div className="order-3 lg:order-none lg:col-start-3 flex flex-col gap-y-4 min-w-0">
							<div className="hidden lg:block">
								<LockedFeatureOverlay locked={isPlanEnded} showLock>
									<CareTeamCards
										members={careProfile.careTeam}
										tierFlags={careProfile.tierFlags}
										language={language}
										country={country ?? ''}
										onMessage={handleMessage}
										onBook={handleBook}
									/>
								</LockedFeatureOverlay>
							</div>
							{/* Shop and support chat stay usable while the plan is ended (Figma). */}
							<UpsellBlock title={careProfile.upsell.title} items={careProfile.upsell.items} language={language} />
							<LockedFeatureOverlay locked={isPlanEnded}>
								<QuickLinks
									quickLinks={careProfile.quickLinks.filter((link: any) => link.key !== 'support-chat')}
									language={language}
									onOpenTreatmentDocuments={openTreatmentDocuments}
								/>
							</LockedFeatureOverlay>
							{supportChatLink && (
								<CareProfileGradientButton
									id="care-profile-support-chat-button"
									onClick={() => { setChatRole('support'); setIsChatOpen(true); setChatChannel('support');}}
									rounded="rounded-lg"
									className="py-[17px] px-[18px] text-[16px]"
									icon={<BsChatDots size={25} />}
								>
									{translator.translate(language, 'my_account', 'CareProfile/QuickLinks/SupportChat')}
								</CareProfileGradientButton>
							)}
						</div>
					</div>


					{/* Keyed on channel so switching threads remounts and refetches rather than
					    briefly showing the previous thread's messages. */}
					<ChatWithDoctorSheet
						key={chatChannel ?? 'none'}
						country={country ?? ''}
						language={language}
						channel={chatChannel ?? 'doctor'}
						isOpen={isChatOpen}
						onClose={() => { setIsChatOpen(false); setChatChannel(null); setChatRole(undefined); }}
						role={chatRole}
					/>

					<BookConsultationSheet
						isOpen={isBookingOpen}
						onClose={() => setIsBookingOpen(false)}
						country={country ?? ''}
						language={language}
						onBooked={() => fetchCareProfile({ soft: true })}
						preferredDoctorID={preferredDoctorID}
						providerRole={bookingRole}
					/>

					<NutritionSummarySheet
						isOpen={Boolean(summaryEvent)}
						onClose={() => setSummaryEvent(null)}
						language={language}
						title={summaryEvent?.title ?? ''}
						dateISO={summaryEvent?.date ?? null}
						doctorName={careProfile.careTeam.find((member: any) => member.name)?.name ?? null}
						onMessageDoctor={() => { setChatRole('doctor'); setIsChatOpen(true);setChatChannel('doctor');}}
					/>

					<LogWeightSheet
						isOpen={isLogWeightOpen}
						onClose={() => setIsLogWeightOpen(false)}
						onSubmit={handleLogWeightSubmit}
						defaultWeight={careProfile.weight?.progress.currentKg ?? null}
						language={language}
						loading={isSubmittingWeight}
						submitError={logWeightError}
						planStartDate={careProfile?.plan?.startDate}
						weightUnit={weightUnit}
						allowUnitChange={allowWeightUnitChange}
						onWeightUnitChange={setWeightUnit}
					/>

					<SetGoalSheet
						isOpen={isSetGoalOpen}
						onClose={() => setIsSetGoalOpen(false)}
						onSubmit={handleSetGoalSubmit}
						currentKg={careProfile.weight?.progress.currentKg ?? null}
						language={language}
						loading={isSubmittingGoal}
						submitError={setGoalError}
						weightUnit={weightUnit}
						allowUnitChange={allowWeightUnitChange}
						onWeightUnitChange={setWeightUnit}
					/>

					<TreatmentDocumentsSheet
						isOpen={isDocumentsOpen}
						onClose={() => setIsDocumentsOpen(false)}
						country={country ?? ''}
						language={language}
						data={treatmentDocuments}
						loading={isLoadingDocuments}
					/>
				</div>
			</div>
			<CareProfileTour
				language={language}
				firstName={firstName}
				isMonthlyPlan={Boolean(careProfile?.tierFlags?.isMonthlyPlan)}
				phase={tour.phase}
				currentStep={tour.currentStep}
				stepIndex={tour.stepIndex}
				totalSteps={tour.activeSteps.length}
				onStart={tour.start}
				onNext={tour.next}
				onBack={tour.back}
				onSkip={tour.skip}
				onComplete={tour.complete}
			/>
		</>
	);
};

export default MyAccountCareProfile;
